import React, { forwardRef } from "react";
import type { BaseProps } from "@purpur/common-types";
import { VisuallyHidden } from "@purpur/visually-hidden";
import c from "classnames/bind";

import styles from "./pagination-page-trigger.module.scss";
import { type InternalLinkProps, type InternalNoLinkProps, type Link } from "./types";

const cx = c.bind(styles);

export type PaginationPageTriggerProps = Omit<BaseProps<"li">, "children"> & {
  linkElement: Link;
  disabled?: boolean;
  onPageChange: (page: number) => void;
  pageNumber: number;
  selected: boolean;
  stepNumberPrefix: string;
} & (InternalLinkProps | InternalNoLinkProps);

const rootClassName = "purpur-pagination-page-trigger";

export const PaginationPageTrigger = forwardRef<HTMLLIElement, PaginationPageTriggerProps>(
  (
    {
      ["data-testid"]: dataTestId = "purpur-pagination-page-trigger",
      linkElement: LinkElement,
      asLink,
      className,
      disabled,
      hrefGetter,
      onPageChange,
      pageNumber,
      selected,
      stepNumberPrefix,
      ...props
    },
    ref
  ) => {
    const classes = cx(className, rootClassName);

    return (
      <li className={classes} data-testid={dataTestId} ref={ref} {...props}>
        {asLink ? (
          disabled ? (
            <span
              className={cx(
                `${rootClassName}__trigger-item`,
                `${rootClassName}__trigger-item--page-trigger`,
                `${rootClassName}__trigger-item--sm`,
                `${rootClassName}__trigger-item--icon-only`,
                `${rootClassName}__trigger-item--disabled`
              )}
              aria-current={selected ? "page" : undefined}
              data-testid={`${dataTestId}-disabled-link`}
            >
              <VisuallyHidden>{`${stepNumberPrefix} `}</VisuallyHidden>
              {pageNumber}
            </span>
          ) : (
            <LinkElement
              className={cx(
                `${rootClassName}__trigger-item`,
                `${rootClassName}__trigger-item--page-trigger`,
                `${rootClassName}__trigger-item--sm`,
                `${rootClassName}__trigger-item--icon-only`,
                { [`${rootClassName}__trigger-item--selected`]: selected }
              )}
              aria-current={selected ? "page" : undefined}
              data-testid={`${dataTestId}-link`}
              href={hrefGetter(pageNumber)}
              onClick={() => onPageChange(pageNumber)}
            >
              <VisuallyHidden>{`${stepNumberPrefix} `}</VisuallyHidden>
              {pageNumber}
            </LinkElement>
          )
        ) : (
          <button
            aria-disabled={disabled}
            className={cx(
              `${rootClassName}__trigger-item`,
              `${rootClassName}__trigger-item--page-trigger`,
              `${rootClassName}__trigger-item--sm`,
              `${rootClassName}__trigger-item--icon-only`,
              { [`${rootClassName}__trigger-item--selected`]: selected }
            )}
            data-testid={`${dataTestId}-button`}
            aria-current={selected ? "page" : undefined}
            disabled={disabled}
            onClick={() => onPageChange(pageNumber)}
            type="button"
          >
            <VisuallyHidden>{`${stepNumberPrefix} `}</VisuallyHidden>
            {pageNumber}
          </button>
        )}
      </li>
    );
  }
);

PaginationPageTrigger.displayName = "PaginationPageTrigger";
